import { apiClient, setAccessToken } from './apiClient'
import {
  clearAuthStorage,
  getAccessToken,
  getRefreshToken as readRefreshToken,
  getTwoFactorPendingRaw,
  purgeLegacyAuthStorage,
  setRefreshToken,
  setTwoFactorPendingRaw
} from './authStorage'
import { sessionLockService } from './sessionLockService'
import { tokenRefreshService } from './tokenRefreshService'
import { usersService } from './usersService'

purgeLegacyAuthStorage()

const completeLogin = async (data) => {
  setAccessToken(data.accessToken)
  if (data.refreshToken) {
    setRefreshToken(data.refreshToken)
  }
  if (data.user) {
    usersService.setSessionUser(data.user)
    await usersService.refreshCurrentUserPhoto()
  }
  sessionLockService.resetAfterLogin()
  tokenRefreshService.start()
}

export const authService = {
  async login(email, password) {
    const data = await apiClient.post('/auth/login', { email, password }, { auth: false })

    if (data.twoFactorRequired) {
      setTwoFactorPendingRaw(JSON.stringify({
        challengeToken: data.challengeToken,
        maskedEmail: data.maskedEmail || email,
        message: data.message || ''
      }))
      return {
        twoFactorRequired: true,
        challengeToken: data.challengeToken,
        maskedEmail: data.maskedEmail,
        message: data.message
      }
    }

    authService.clearTwoFactorPending()
    await completeLogin(data)
    return data
  },

  async verifyTwoFactor(code) {
    const pending = authService.getTwoFactorPending()
    if (!pending?.challengeToken) {
      throw new Error('Session de vérification expirée. Reconnectez-vous.')
    }

    const data = await apiClient.post(
      '/auth/verify-2fa',
      { challengeToken: pending.challengeToken, code: String(code).trim() },
      { auth: false }
    )

    authService.clearTwoFactorPending()
    await completeLogin(data)
    return data
  },

  getTwoFactorPending() {
    try {
      const raw = getTwoFactorPendingRaw()
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  },

  clearTwoFactorPending() {
    setTwoFactorPendingRaw(null)
  },

  async unlock(password) {
    const refreshed = await tokenRefreshService.refreshIfNeeded()
    if (!refreshed && !getAccessToken()) {
      throw new Error('Session expirée. Reconnectez-vous.')
    }
    await apiClient.post('/auth/unlock', { password })
    sessionLockService.unlockSession()
  },

  async forgotPassword(email) {
    return apiClient.post('/auth/forgot-password', { email }, { auth: false })
  },

  logout() {
    setAccessToken(null)
    setRefreshToken(null)
    authService.clearTwoFactorPending()
    usersService.clearPhotoCache()
    usersService.setSessionUser(null)
    clearAuthStorage()
    purgeLegacyAuthStorage()
    tokenRefreshService.stop()
    sessionLockService.clear()
  },

  isAuthenticated() {
    const token = getAccessToken()
    return typeof token === 'string' && token.length > 0
  },

  getRefreshToken() {
    return readRefreshToken()
  }
}
